import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, MessageCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import VentModal from './VentModal';
import VentList from './VentList';
import { analytics } from '../utils/analytics';
import { TarotCardMini, tarotStyles } from './CommunitiesRow';
import SageBackstoryModal from './SageBackstoryModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommunityDebatePage = ({ sessionId }) => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [community, setCommunity] = useState(null);
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasVoted, setHasVoted] = useState(false);
  const [userVote, setUserVote] = useState(null);
  const [voteAnimation, setVoteAnimation] = useState(null);
  const [showVentModal, setShowVentModal] = useState(false);
  const [showBackstory, setShowBackstory] = useState(false);
  const [vents, setVents] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [commRes, topicRes] = await Promise.all([
        axios.get(`${API}/communities`),
        axios.get(`${API}/communities/${slug}/topic/today`)
      ]);
      const comm = commRes.data.find(c => c.slug === slug);
      setCommunity(comm);
      setTopic(topicRes.data);
    } catch (err) {
      toast.error('Failed to load community topic');
      if (process.env.NODE_ENV === 'development') console.warn('[Vent:community]', err?.message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const checkUserVote = useCallback(async () => {
    if (!topic) return;
    try {
      const res = await axios.get(`${API}/topic/${topic.id}/user-vote?session_id=${sessionId}`);
      setHasVoted(res.data.has_voted);
      setUserVote(res.data.vote_type);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('[Vent:vote]', err?.message);
    }
  }, [topic, sessionId]);

  const fetchVents = useCallback(async () => {
    if (!topic) return;
    try {
      const res = await axios.get(`${API}/vents/${topic.id}`);
      setVents(res.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('[Vent:vents]', err?.message);
    }
  }, [topic]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (topic) { checkUserVote(); fetchVents(); } }, [topic, checkUserVote, fetchVents]);

  const handleVote = useCallback(async (voteType) => {
    if (hasVoted) return toast.info('Already voted');
    try {
      setVoteAnimation(voteType);
      await axios.post(`${API}/topic/vote`, { topic_id: topic.id, vote_type: voteType, session_id: sessionId });
      setHasVoted(true);
      setUserVote(voteType);
      setTopic(prev => ({
        ...prev,
        votes_for: voteType === 'for' ? prev.votes_for + 1 : prev.votes_for,
        votes_against: voteType === 'against' ? prev.votes_against + 1 : prev.votes_against
      }));
      analytics.vote(voteType);
      toast.success(voteType === 'for' ? 'Voted FOR!' : 'Voted AGAINST!');
      setTimeout(() => setVoteAnimation(null), 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to vote');
      setVoteAnimation(null);
    }
  }, [hasVoted, topic, sessionId]);

  const handleVentSubmit = useCallback(async (content) => {
    try {
      const res = await axios.post(`${API}/vents`, { topic_id: topic.id, content, session_id: sessionId });
      setVents(prev => [res.data, ...prev]);
      setShowVentModal(false);
      analytics.vent(res.data.is_suggestion);
      toast.success('Posted anonymously!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    }
  }, [topic, sessionId]);

  const totalVotes = (topic?.votes_for || 0) + (topic?.votes_against || 0);
  const forPct = totalVotes > 0 ? Math.round((topic?.votes_for / totalVotes) * 100) : 50;
  const againstPct = totalVotes > 0 ? 100 - forPct : 50;

  if (loading) {
    return (
      <div className="min-h-screen bg-vent-bg flex items-center justify-center">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }} className="w-12 h-12 border-2 border-vent-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vent-bg bg-pattern">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          <div className="flex items-center gap-2">
            {community && <TarotCardMini community={community} size="sm" />}
          </div>
        </div>
      </nav>

      <main className="pt-20 sm:pt-24 pb-44 sm:pb-32 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          {/* Community header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6 flex flex-col items-center">
            {community && <TarotCardMini community={community} size="md" />}
            <p className="text-zinc-500 text-sm mt-3">{community?.description}</p>
          </motion.div>

          {/* Topic Card */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="glass rounded-2xl sm:rounded-3xl p-5 sm:p-8 mb-6" data-testid="community-topic-card">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight" data-testid="community-topic-title">{topic?.title}</h2>
            <p className="text-zinc-400 text-base mb-6">{topic?.description}</p>

            {/* Vote buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <motion.button
                whileHover={{ scale: hasVoted ? 1 : 1.02 }}
                whileTap={{ scale: hasVoted ? 1 : 0.98 }}
                onClick={() => handleVote('for')}
                disabled={hasVoted}
                className={`flex-1 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-heading font-bold tracking-widest text-sm uppercase transition-all ${
                  userVote === 'for' ? 'bg-vent-for text-black' : hasVoted ? 'border-2 border-zinc-700 text-zinc-600 cursor-not-allowed' : 'btn-for'
                }`}
                data-testid="community-vote-for"
              >
                <AnimatePresence>
                  {voteAnimation === 'for' && <motion.span initial={{ scale: 0 }} animate={{ scale: [1, 1.5, 1] }} className="inline-block mr-2">✓</motion.span>}
                </AnimatePresence>
                FOR
              </motion.button>
              <motion.button
                whileHover={{ scale: hasVoted ? 1 : 1.02 }}
                whileTap={{ scale: hasVoted ? 1 : 0.98 }}
                onClick={() => handleVote('against')}
                disabled={hasVoted}
                className={`flex-1 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-heading font-bold tracking-widest text-sm uppercase transition-all ${
                  userVote === 'against' ? 'bg-vent-against text-white' : hasVoted ? 'border-2 border-zinc-700 text-zinc-600 cursor-not-allowed' : 'btn-against'
                }`}
                data-testid="community-vote-against"
              >
                <AnimatePresence>
                  {voteAnimation === 'against' && <motion.span initial={{ scale: 0 }} animate={{ scale: [1, 1.5, 1] }} className="inline-block mr-2">✓</motion.span>}
                </AnimatePresence>
                AGAINST
              </motion.button>
            </div>

            {/* Results bar */}
            {hasVoted && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-6 pt-6 border-t border-white/10">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-vent-for">{forPct}% FOR</span>
                  <span className="text-vent-against">{againstPct}% AGAINST</span>
                </div>
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden flex">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${forPct}%` }} transition={{ duration: 1 }} className="bg-vent-for" />
                  <motion.div initial={{ width: 0 }} animate={{ width: `${againstPct}%` }} transition={{ duration: 1 }} className="bg-vent-against" />
                </div>
                <p className="text-center text-zinc-500 text-sm mt-2">{totalVotes} votes</p>
              </motion.div>
            )}
          </motion.div>

          {/* Vent button */}
          <div className="fixed bottom-0 left-0 right-0 p-4 pb-24 sm:pb-4 sm:p-0 sm:relative sm:mt-6 bg-gradient-to-t from-black/90 via-black/60 to-transparent sm:from-transparent sm:via-transparent sm:bg-none z-50" style={{ paddingBottom: 'max(6rem, calc(env(safe-area-inset-bottom, 0px) + 5rem))' }}>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowVentModal(true)}
              className="w-full btn-vent py-4 sm:py-5 rounded-full font-heading font-bold tracking-[0.15em] sm:tracking-[0.2em] text-base sm:text-lg flex items-center justify-center gap-2 shadow-lg"
              data-testid="community-vent-btn"
            >
              <MessageCircle className="w-5 h-5" /> VENT
            </motion.button>
          </div>

          {/* Sage portrait — fills the empty space below Vent. Click for backstory. */}
          {community && tarotStyles[community.slug]?.sage && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-8 flex flex-col items-center"
              data-testid="community-sage-portrait"
            >
              <motion.button
                onClick={() => setShowBackstory(true)}
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                className="relative w-44 h-44 sm:w-52 sm:h-52 rounded-full overflow-hidden border-2 cursor-pointer group"
                style={{
                  borderColor: `${tarotStyles[community.slug].accent}40`,
                  boxShadow: `0 0 40px ${tarotStyles[community.slug].accent}30, 0 8px 32px rgba(0,0,0,0.5)`,
                }}
                data-testid="sage-portrait-btn"
                aria-label={`Learn about the ${community.name} Sage`}
              >
                <img
                  src={tarotStyles[community.slug].sage}
                  alt={`${community.name} Sage`}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  style={{ objectPosition: tarotStyles[community.slug].objectPosition || 'center' }}
                  draggable={false}
                />
                {/* Subtle vignette */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                {/* Animated glow ring */}
                <motion.div
                  className="absolute -inset-0.5 rounded-full pointer-events-none"
                  style={{ boxShadow: `inset 0 0 24px ${tarotStyles[community.slug].accent}50` }}
                  animate={{ opacity: [0.4, 0.8, 0.4] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                />
                {/* Hover hint */}
                <div className="absolute inset-x-0 bottom-0 py-2 bg-black/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center">
                  <span className="text-white text-[10px] font-heading tracking-[0.25em] uppercase">Tap for story</span>
                </div>
              </motion.button>
              <p className="mt-3 text-xs font-heading tracking-[0.25em] uppercase" style={{ color: tarotStyles[community.slug].accent }}>
                The {community.name} Sage
              </p>
              <button
                onClick={() => setShowBackstory(true)}
                className="text-zinc-600 text-xs italic mt-1 max-w-xs text-center leading-relaxed hover:text-zinc-400 transition-colors"
                data-testid="sage-backstory-hint"
              >
                Tap to hear their tale →
              </button>
            </motion.div>
          )}

          {/* Vents */}
          {vents.length > 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-8">
              <h3 className="font-heading text-lg font-bold text-zinc-300 mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-vent-primary" /> Community Vents
              </h3>
              <VentList vents={vents} sessionId={sessionId} onUpdate={fetchVents} />
            </motion.div>
          )}
        </div>
      </main>

      <VentModal isOpen={showVentModal} onClose={() => setShowVentModal(false)} onSubmit={handleVentSubmit} />
      {community && (
        <SageBackstoryModal
          isOpen={showBackstory}
          onClose={() => setShowBackstory(false)}
          sage={tarotStyles[community.slug]?.backstory}
          accent={tarotStyles[community.slug]?.accent}
          communityName={community.name}
        />
      )}
    </div>
  );
};

export default CommunityDebatePage;
